import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export type MasterCertificate = {
  databaseId: string;
  name: string;
  insuredDatabaseId: string;
  insuredEmail: string;
  insuredFirstName: string;
  insuredLastName: string;
  insuredCommercialName: string;
  createDate: string;
  changeDate: string;
  active: boolean;
  visible: boolean;
  useInHoaunit: boolean;
  parentDatabaseId: string | null;
  parentName: string;
  acordFormNumber: number;
  blanketAdditionalInterestTypes?: number[];
  blanketWaiverofSubrogation?: number[];
  buildingIds: string[];
  policies: Array<{
    databaseId: string;
    number: string;
    effectiveDate: string;
    expirationDate: string;
    linesOfBusiness: string[];
    carrierName: string | null;
  }>;
  coverages: Array<{
    databaseId: string;
    policyNumber: string;
    naiCid: string | null;
    effectiveDate: string | null;
    expirationDate: string | null;
    type: number;
    acordFormNumber: number;
    coveragesAndLimits: string;
    signatureId: string | null;
  }>;
};

export function useMasterCertificates(insuredDatabaseId?: string, options?: { skip?: number; top?: number; activeOnly?: boolean; search?: string }) {
  const skip = options?.skip ?? 0;
  const top = options?.top ?? 100;
  const activeOnly = options?.activeOnly ?? false;
  const search = options?.search ?? '';

  const [certificates, setCertificates] = useState<MasterCertificate[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchKey, setFetchKey] = useState(0);
  const navigate = useNavigate();

  const refetch = () => setFetchKey(k => k + 1);

  useEffect(() => {
    if (!insuredDatabaseId) return;
    setIsLoading(true);
    setError(null);
    const params = new URLSearchParams({
      insuredDatabaseId,
      skip: String(skip),
      top: String(top),
    });
    if (activeOnly) params.set('activeOnly', 'true');
    if (search.trim()) params.set('search', search.trim());
    const url = `${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/master-certificates?${params}`;
    fetch(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('igtoken')}`,
        NCToken: localStorage.getItem('NCToken') || '',
      },
    })
      .then((res) => {
        if (res.status === 401) {
          window.localStorage.clear();
          navigate('/');
          throw new Error('Unauthorized');
        }
        if (!res.ok) throw new Error('Failed to fetch master certificates');
        return res.json();
      })
      .then((data) => {
        setCertificates(data.value || []);
        setTotalCount(data['@odata.count'] ?? 0);
        setIsLoading(false);
      })
      .catch((err) => {
        if (err.message === 'Unauthorized') return;
        setError(err.message || 'Unknown error');
        setIsLoading(false);
      });
  }, [insuredDatabaseId, skip, top, activeOnly, search, fetchKey]);

  return { certificates, totalCount, isLoading, error, refetch };
}
